// React & Hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Utilities & Helpers
import { transformRoute, useTranslate } from "helpers";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// API & Types
import { DataWithPagination } from "services/api";
import { Pagination } from "services/search";
import { initialDataWithPagination } from "services/api/consts";
import { getTehnicalInspectionsAPI } from "services/api/calculations/tehnicalinspection/api";
import {
  TehnicalInspectionRow,
  TehnicalInspectionSearchFormValues,
} from "services/api/calculations/tehnicalinspection/types";

// Routing
import { Routes as TehnicalInspectionRoutes, Routes } from "ui/modules/calculation/navigation";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import TabMenu from "../TabMenu/TabMenu";
import TehnicalInspectionEditCard from "../TehnicalInspectionEditCard/TehnicalInspectionEditCard";
import TehnicalInspectionSearchFilters from "./components/TehnicalInspectionSearchFilters/TehnicalInspectionSearchFilters";
import TehnicalInspectionSearchResults from "./components/TehnicalInspectionSearchResults/TehnicalInspectionSearchResults";

// Constants
import {
  CALCULATION_TEHNICAL_INSPECTION_CREATE_PAGE_TITLE,
  CALCULATION_TEHNICAL_INSPECTION_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Component Types
import {
  TehnicalInspectionSearchPageCmp,
  TehnicalInspectionSearchPageProps,
} from "./types";


const TehnicalInspectionSearchPage: TehnicalInspectionSearchPageCmp = (
  props: TehnicalInspectionSearchPageProps
) => {
  const { t } = useTranslate();
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<TehnicalInspectionRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [tehnicalInspectionId, setTehnicalInspectionId] = useState<
    string | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getTehnicalInspectionsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(
        TehnicalInspectionRoutes.CalculationTehnicalInspectionDetailPageRouter,
        {
          tehnicalInspectionId: id,
        }
      )
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setTehnicalInspectionId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: TehnicalInspectionSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setTehnicalInspectionId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_TEHNICAL_INSPECTION_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_TEHNICAL_INSPECTION_SEARCH_PAGE_TITLE.icon}
              iconColor={
                CALCULATION_TEHNICAL_INSPECTION_SEARCH_PAGE_TITLE.iconColor
              }
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <TehnicalInspectionSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.tehnicalinspection.search.noresults"
              )}
            />
          ) : (
            <TehnicalInspectionSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_TEHNICAL_INSPECTION_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <TehnicalInspectionEditCard
              tehnicalInspectionId={tehnicalInspectionId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

TehnicalInspectionSearchPage.route =
  Routes.CalculationTehnicalInspectionSearchPageRouter;

export default TehnicalInspectionSearchPage;
